/* Two-iPhone battery comparison for the OzMobiles battery health article.
   Mounts into every element with the class pc-battery-compare (a class, because Shopify's article editor strips
   data-* attributes from the body). The data file's URL comes from this script tag's
   data-battery-compare-data attribute. Built from the Phonecheck export by build_widget_data.py: each model's measured
   battery health by charge cycles, and each phone's power draw on a Light, Normal and Heavy day (from lab measurements,
   see research/usage_profiles.py), which sets both its cycles a day and how long a charge lasts. */
(function () {
  "use strict";

  var SCRIPT = document.currentScript || document.querySelector("script[data-battery-compare-data]");
  var SVG_NS = "http://www.w3.org/2000/svg";
  var DEFAULTS = { a: "iPhone 11", b: "iPhone 16 Pro", usage: "normal", metric: "health" };
  var USAGE = [["light", "Light"], ["normal", "Normal"], ["heavy", "Heavy"]];
  var METRICS = [["health", "Battery health"], ["time", "Screen time"]];
  var DAYS_PER_YEAR = 365.25;
  var COLORS = { a: "#63388c", b: "#3d7d0f" };
  var MUTED = "#6b6b6b";
  var DASH = "7 6";
  var SHAPES = { a: "circle", b: "square" };
  var STEPS_PER_YEAR = 48;
  var uid = 0;

  function el(tag, props, children) {
    var node = document.createElement(tag);
    Object.keys(props || {}).forEach(function (key) {
      if (key === "className" || key === "textContent" || key === "htmlFor" || key === "value" || key === "checked") {
        node[key] = props[key];
      } else {
        node.setAttribute(key, props[key]);
      }
    });
    (children || []).forEach(function (child) {
      node.appendChild(typeof child === "string" ? document.createTextNode(child) : child);
    });
    return node;
  }

  function svg(tag, attrs, text) {
    var node = document.createElementNS(SVG_NS, tag);
    Object.keys(attrs).forEach(function (key) { node.setAttribute(key, attrs[key]); });
    if (text !== undefined) node.textContent = text;
    return node;
  }

  function roundTo(value, step) {
    return Math.round(value / step) * step;
  }

  function thousands(value) {
    return String(value).replace(/\B(?=(\d{3})+(?!\d))/g, ",");
  }

  function marker(shape, cx, cy, color, filled) {
    var common = { fill: filled ? color : "#ffffff", stroke: filled ? "#ffffff" : color, "stroke-width": 2 };
    if (shape === "circle") {
      return svg("circle", Object.assign({ cx: cx, cy: cy, r: 4.5 }, common));
    }
    return svg("rect", Object.assign({ x: cx - 4.5, y: cy - 4.5, width: 9, height: 9 }, common));
  }

  function legendKey(color, options) {
    options = options || {};
    var width = options.line === false ? 12 : 30;
    var node = svg("svg", { class: "pcbc-key", width: width, height: 12, viewBox: "0 0 " + width + " 12", "aria-hidden": "true", focusable: "false" });
    if (options.line !== false) {
      node.appendChild(svg("line", { x1: 1, x2: width - 1, y1: 6, y2: 6, stroke: color, "stroke-width": 3,
        "stroke-linecap": options.dashed ? "butt" : "round", "stroke-dasharray": options.dashed ? DASH : "none" }));
    }
    if (options.shape) node.appendChild(marker(options.shape, width / 2, 6, color, true));
    return node;
  }

  function shortName(name) {
    return name.replace(/^iPhone /, "").replace(/ \((\w+) generation\)$/, " ($1 gen)");
  }

  function longShort(long, short) {
    return [el("span", { className: "pcbc-long", textContent: long }), el("span", { className: "pcbc-short", textContent: short })];
  }

  function modelLabel(model, fullName) {
    var rest = model.name.indexOf("iPhone ") === 0 ? model.name.slice(7) : null;
    var link = el("a", { href: model.url });
    if (rest === null || fullName) {
      link.textContent = model.name;
      return link;
    }
    link.appendChild(el("span", { className: "pcbc-brand", textContent: "iPhone " }));
    if (shortName(model.name) === rest) {
      link.appendChild(document.createTextNode(rest));
    } else {
      longShort(rest, shortName(model.name)).forEach(function (node) { link.appendChild(node); });
    }
    return link;
  }

  // Both phones get the same day. Its energy (each phone's own watts for that kind of use) over the phone's battery sets
  // its cycles a day, and the battery's energy over those watts sets how long a full charge lasts.
  function series(model, state) {
    var day = model.use[state.usage];
    return { model: model, perDay: day.charges_a_day, factor: state.metric === "time" ? day.hours_a_charge / 100 : 1 };
  }

  function cyclesAt(s, years) {
    return s.perDay * DAYS_PER_YEAR * years;
  }

  // Battery health at a number of years, and whether that part of the line is measured or estimated. Measured needs a
  // measured cycle band and a year the model has reached: no model is measured past its own age.
  function valueAt(s, years) {
    var points = s.model.points;
    var cycles = cyclesAt(s, years);
    if (cycles > points[points.length - 1][0]) return null;
    for (var i = 0; i < points.length - 1; i++) {
      var p0 = points[i], p1 = points[i + 1];
      if (cycles <= p1[0]) {
        var f = (cycles - p0[0]) / (p1[0] - p0[0]);
        return { value: (p0[1] + (p1[1] - p0[1]) * f) * s.factor, measured: p0[2] && p1[2] && years <= s.model.measured_years, cycles: cycles };
      }
    }
    return null;
  }

  // The figure where our data ends: past it a battery can only be lower, so later years read "Below" this.
  function endValue(s) {
    var points = s.model.points;
    return points[points.length - 1][1] * s.factor;
  }

  function segments(s, maxYears) {
    var runs = [], steps = maxYears * STEPS_PER_YEAR;
    for (var i = 0; i <= steps; i++) {
      var years = i / STEPS_PER_YEAR, point = valueAt(s, years);
      if (!point) break;
      var last = runs[runs.length - 1];
      if (last && last.measured === point.measured) {
        last.points.push([years, point.value]);
      } else {
        var start = last ? [last.points[last.points.length - 1]] : [];
        runs.push({ measured: point.measured, points: start.concat([[years, point.value]]) });
      }
    }
    return runs;
  }

  function batteryText(model) {
    return thousands(model.mah) + " mAh";
  }

  // mAh with the screen time a full new battery gives on the chosen day under it.
  function batteryMeta(model, usage) {
    return el("span", { className: "pcbc-picker-meta" }, [
      el("span", { className: "pcbc-meta-mah", textContent: batteryText(model) }),
      el("span", { className: "pcbc-meta-time", textContent: model.use[usage].hours_a_charge.toFixed(1) + " h screen time" })
    ]);
  }

  function formatValue(value, metric) {
    return metric === "time" ? value.toFixed(1) + " h" : Math.round(value) + "%";
  }

  function mount(host, data) {
    var models = data.models, byName = {};
    var maxYears = data.years;
    var tableYears = [];
    for (var y = 1; y <= maxYears; y++) tableYears.push(y);
    models.forEach(function (model) { byName[model.name] = model; });
    var state = {
      a: byName[DEFAULTS.a] ? DEFAULTS.a : models[0].name,
      b: byName[DEFAULTS.b] ? DEFAULTS.b : models[models.length - 1].name,
      usage: DEFAULTS.usage,
      metric: DEFAULTS.metric
    };
    var id = "pcbc" + (++uid);
    var refreshers = [];   // each picker's button, redrawn when the day changes its hours

    // A searchable model picker with product photos: a button showing the chosen model, which opens into a search box
    // over a list (combobox pattern: arrows move, Enter picks, Escape closes).
    function picker(key, label) {
      var listId = id + "-" + key + "-list";
      var field = el("div", { className: "pcbc-picker" });
      var button = el("button", { type: "button", className: "pcbc-picker-button", id: id + "-" + key,
        "aria-haspopup": "listbox", "aria-expanded": "false" });
      var search = el("input", { type: "text", className: "pcbc-picker-search", role: "combobox", "aria-autocomplete": "list",
        "aria-expanded": "true", "aria-controls": listId, "aria-label": label, placeholder: "Search iPhones",
        autocomplete: "off", autocapitalize: "off", spellcheck: "false" });
      var list = el("ul", { className: "pcbc-picker-list", role: "listbox", id: listId, "aria-label": label });
      var shown = [], active = -1, isOpen = false;

      function fill() {
        var model = byName[state[key]];
        button.textContent = "";
        button.appendChild(el("img", { src: model.image, alt: "", width: 32, height: 32 }));
        var short = model.name.replace(/ \((\w+) generation\)$/, " ($1 gen)");
        button.appendChild(el("span", { className: "pcbc-picker-value" }, short === model.name ? [model.name] : longShort(model.name, short)));
        button.appendChild(batteryMeta(model, state.usage));
      }

      function keepVisible(node) {
        if (node.offsetTop < list.scrollTop) {
          list.scrollTop = node.offsetTop - 6;
        } else if (node.offsetTop + node.offsetHeight > list.scrollTop + list.clientHeight) {
          list.scrollTop = node.offsetTop + node.offsetHeight - list.clientHeight + 6;
        }
      }

      function setActive(index) {
        active = index;
        Array.prototype.forEach.call(list.children, function (node, i) { node.classList.toggle("is-active", i === index); });
        var node = shown[index] && list.children[index];
        if (node) {
          keepVisible(node);
          search.setAttribute("aria-activedescendant", node.id);
        } else {
          search.removeAttribute("aria-activedescendant");
        }
      }

      function renderList() {
        var query = search.value.trim().toLowerCase();
        shown = models.filter(function (model) { return model.name.toLowerCase().indexOf(query) !== -1; });
        list.textContent = "";
        if (!shown.length) list.appendChild(el("li", { className: "pcbc-picker-empty", textContent: "No iPhone matches that" }));
        shown.forEach(function (model, i) {
          var option = el("li", { className: "pcbc-picker-option", role: "option", id: listId + "-" + i,
            "aria-selected": String(model.name === state[key]) }, [
            el("img", { src: model.image, alt: "", width: 40, height: 40, loading: "lazy" }),
            el("span", { className: "pcbc-picker-name", textContent: model.name }),
            batteryMeta(model, state.usage)
          ]);
          option.addEventListener("mousedown", function (event) { event.preventDefault(); });
          option.addEventListener("click", function () { choose(model.name); });
          option.addEventListener("mousemove", function () { if (active !== i) setActive(i); });
          list.appendChild(option);
        });
      }

      function outside(event) {
        if (!field.contains(event.target)) close(false);
      }

      function open() {
        if (isOpen) return;
        isOpen = true;
        search.value = "";
        renderList();
        button.hidden = true;
        search.hidden = false;
        list.hidden = false;
        button.setAttribute("aria-expanded", "true");
        search.focus();
        setActive(shown.map(function (model) { return model.name; }).indexOf(state[key]));
        document.addEventListener("pointerdown", outside, true);
      }

      function close(focusButton) {
        if (!isOpen) return;
        isOpen = false;
        search.hidden = true;
        list.hidden = true;
        button.hidden = false;
        button.setAttribute("aria-expanded", "false");
        document.removeEventListener("pointerdown", outside, true);
        if (focusButton) button.focus();
      }

      function choose(name) {
        state[key] = name;
        close(true);
        fill();
        render();
      }

      button.addEventListener("click", open);
      button.addEventListener("keydown", function (event) {
        if (event.key === "ArrowDown" || event.key === "ArrowUp") {
          event.preventDefault();
          open();
        }
      });
      search.addEventListener("input", function () {
        renderList();
        setActive(shown.length ? 0 : -1);
      });
      search.addEventListener("keydown", function (event) {
        if (event.key === "ArrowDown" || event.key === "ArrowUp") {
          event.preventDefault();
          if (shown.length) setActive((active + (event.key === "ArrowDown" ? 1 : shown.length - 1)) % shown.length);
        } else if (event.key === "Enter") {
          event.preventDefault();
          if (shown[active]) choose(shown[active].name);
        } else if (event.key === "Escape") {
          event.preventDefault();
          close(true);
        } else if (event.key === "Tab") {
          close(false);
        }
      });

      search.hidden = true;
      list.hidden = true;
      fill();
      refreshers.push(fill);
      field.appendChild(button);
      field.appendChild(search);
      field.appendChild(list);
      var swatch = legendKey(COLORS[key], { shape: SHAPES[key] });
      return el("div", { className: "pcbc-field" }, [el("label", { className: "pcbc-label", htmlFor: id + "-" + key }, [swatch, label]), field]);
    }

    function segmented(key, legend, options, hint) {
      var group = el("div", { className: "pcbc-segments" });
      options.forEach(function (option) {
        var input = el("input", { type: "radio", name: id + "-" + key, value: option[0] });
        input.checked = state[key] === option[0];
        input.addEventListener("change", function () { if (input.checked) { state[key] = option[0]; render(); } });
        group.appendChild(el("label", {}, [input, el("span", { textContent: option[1] })]));
      });
      var children = [el("legend", { className: "pcbc-label", textContent: legend }), group];
      if (hint) children.push(hint);
      return el("fieldset", { className: "pcbc-field" }, children);
    }

    var legend = el("div", { className: "pcbc-legend" });
    var chart = el("div", { className: "pcbc-chart" });
    var tip = el("div", { className: "pcbc-tip", hidden: "" });
    var tableWrap = el("div", { className: "pcbc-table-wrap" });

    var controls = el("div", { className: "pcbc-controls" }, [
      picker("a", "First iPhone"),
      picker("b", "Second iPhone"),
      segmented("usage", "Daily use", USAGE),
      segmented("metric", "Show", METRICS)
    ]);
    host.textContent = "";
    host.appendChild(el("div", { className: "pcbc-card" }, [controls, legend, chart, tableWrap]));
    host.classList.add("pcbc-ready");

    function current() {
      return { a: series(byName[state.a], state), b: series(byName[state.b], state) };
    }

    function renderLegend(all) {
      legend.textContent = "";
      ["a", "b"].forEach(function (key) {
        var cycles = thousands(roundTo(cyclesAt(all[key], 1), 10));
        var text = el("span", {}, [modelLabel(all[key].model, true), ": about " + cycles + " cycles a year"]);
        legend.appendChild(el("span", { className: "pcbc-legend-item" }, [legendKey(COLORS[key], { shape: SHAPES[key] }), text]));
      });
      legend.appendChild(el("span", { className: "pcbc-legend-item pcbc-legend-style" }, [legendKey(MUTED), "Measured"]));
      legend.appendChild(el("span", { className: "pcbc-legend-item pcbc-legend-style" }, [legendKey(MUTED, { dashed: true }), "Estimate"]));
    }

    function renderTable(all) {
      var head = el("tr", {}, [el("th", { textContent: "Model" })]);
      tableYears.forEach(function (year) {
        head.appendChild(el("th", {}, longShort(year === 1 ? "1 year" : year + " years", year === 1 ? "1 yr" : year + " yrs")));
      });
      var body = el("tbody");
      ["a", "b"].forEach(function (key) {
        var s = all[key];
        var name = el("span", { className: "pcbc-row-name" }, [legendKey(COLORS[key], { shape: SHAPES[key], line: false }), modelLabel(s.model)]);
        var row = el("tr", {}, [el("td", {}, [name])]);
        tableYears.forEach(function (year) {
          var point = valueAt(s, year);
          if (!point) {
            row.appendChild(el("td", { className: "pcbc-est", textContent: "Below " + formatValue(endValue(s), state.metric) }));
          } else {
            row.appendChild(el("td", { className: point.measured ? "" : "pcbc-est", textContent: formatValue(point.value, state.metric) }));
          }
        });
        body.appendChild(row);
      });
      tableWrap.textContent = "";
      tableWrap.appendChild(el("table", { className: "pcbc-table" }, [el("thead", {}, [head]), body]));
    }

    function renderChart(all) {
      var width = chart.clientWidth;
      if (!width) return;
      var narrow = width < 480;
      var height = narrow ? 260 : 320;
      var m = { top: 12, right: 20, bottom: 34, left: narrow ? 42 : 50 };
      tip.classList.toggle("pcbc-tip-narrow", narrow);
      var plotW = width - m.left - m.right, plotH = height - m.top - m.bottom;
      if (plotW < 120) return;   // the box can be a few pixels wide mid-layout; the resize observer draws it once it settles
      var runs = { a: segments(all.a, maxYears), b: segments(all.b, maxYears) };
      var values = [];
      ["a", "b"].forEach(function (key) {
        runs[key].forEach(function (run) { run.points.forEach(function (p) { values.push(p[1]); }); });
      });
      var health = state.metric === "health";
      var yMin = health ? Math.min(70, Math.floor(Math.min.apply(null, values) / 5) * 5) : 0;
      var yMax = health ? 100 : Math.max(10, Math.ceil(Math.max.apply(null, values) / 5) * 5);
      var step = health ? 10 : 5;
      var x = function (years) { return m.left + years / maxYears * plotW; };
      var y = function (value) { return m.top + (yMax - value) / (yMax - yMin) * plotH; };

      var root = svg("svg", { width: width, height: height, viewBox: "0 0 " + width + " " + height, role: "img",
        "aria-label": (health ? "Battery health" : "Screen time on a full charge") + " over " + maxYears + " years for " +
          state.a + " and " + state.b + ". The figures are in the table below." });

      if (health && yMin < 80) {
        root.appendChild(svg("rect", { x: m.left, y: y(80), width: plotW, height: y(yMin) - y(80), fill: "#fff1e3" }));
        root.appendChild(svg("text", { x: m.left + 8, y: y(80) + 16, fill: "#a34c00", "font-size": 12 }, "Below 80%"));
      }
      for (var tick = yMin; tick <= yMax + 0.001; tick += step) {
        root.appendChild(svg("line", { x1: m.left, x2: m.left + plotW, y1: y(tick), y2: y(tick), stroke: tick === yMin ? "#c8c8c8" : "#ececec", "stroke-width": 1 }));
        root.appendChild(svg("text", { x: m.left - 8, y: y(tick) + 4, "text-anchor": "end", fill: MUTED, "font-size": 12 },
          health ? tick + "%" : tick + " h"));
      }
      for (var year = 0; year <= maxYears; year++) {
        root.appendChild(svg("text", { x: x(year), y: height - 10, "text-anchor": year === 0 ? "start" : "middle",
          fill: MUTED, "font-size": 12 }, year === 0 ? "New" : (year === 1 ? "1 yr" : year + " yrs")));
      }

      ["a", "b"].forEach(function (key) {
        runs[key].forEach(function (run) {
          var d = run.points.map(function (p, i) { return (i ? "L" : "M") + x(p[0]).toFixed(1) + " " + y(p[1]).toFixed(1); }).join(" ");
          root.appendChild(svg("path", { d: d, fill: "none", stroke: COLORS[key], "stroke-width": 3, "stroke-linejoin": "round",
            "stroke-linecap": run.measured ? "round" : "butt", "stroke-dasharray": run.measured ? "none" : DASH }));
        });
        tableYears.forEach(function (yr) {
          var point = valueAt(all[key], yr);
          if (point) root.appendChild(marker(SHAPES[key], x(yr), y(point.value), COLORS[key], point.measured));
        });
      });

      var guide = svg("line", { y1: m.top, y2: m.top + plotH, stroke: "#9a9a9a", "stroke-width": 1, visibility: "hidden" });
      root.appendChild(guide);
      var overlay = svg("rect", { x: m.left, y: m.top, width: plotW, height: plotH, fill: "transparent" });
      root.appendChild(overlay);

      function show(event) {
        var box = root.getBoundingClientRect();
        var years = Math.max(0, Math.min(maxYears, (event.clientX - box.left - m.left) / plotW * maxYears));
        years = Math.round(years * 10) / 10;
        guide.setAttribute("x1", x(years));
        guide.setAttribute("x2", x(years));
        guide.setAttribute("visibility", "visible");
        tip.textContent = "";
        tip.appendChild(el("strong", { textContent: years === 0 ? "New" : "After " + years + (years === 1 ? " year" : " years") }));
        ["a", "b"].forEach(function (key) {
          var point = valueAt(all[key], years);
          var name = narrow ? all[key].model.name.replace(/ \((\w+) generation\)$/, " ($1 gen)") : all[key].model.name;
          var text = name + ": " + (point ? formatValue(point.value, state.metric) + (point.measured ? "" : " (estimate)") : "below " + formatValue(endValue(all[key]), state.metric));
          tip.appendChild(el("span", { className: "pcbc-tip-row" }, [el("span", { className: "pcbc-dot", style: "background: " + COLORS[key] }), text]));
        });
        tip.hidden = false;
        var left = x(years) + 14;
        if (left + tip.offsetWidth > width) left = x(years) - 14 - tip.offsetWidth;
        tip.style.left = Math.max(0, left) + "px";
      }
      function hide() {
        guide.setAttribute("visibility", "hidden");
        tip.hidden = true;
      }
      overlay.addEventListener("pointermove", show);
      overlay.addEventListener("pointerdown", show);
      overlay.addEventListener("pointerleave", hide);

      chart.textContent = "";
      chart.appendChild(root);
      chart.appendChild(tip);
      tip.hidden = true;
    }

    function render() {
      var all = current();
      refreshers.forEach(function (fill) { fill(); });
      renderLegend(all);
      renderChart(all);
      renderTable(all);
    }

    render();
    if (window.ResizeObserver) {
      var lastWidth = chart.clientWidth;
      new ResizeObserver(function () {
        if (chart.clientWidth !== lastWidth) {
          lastWidth = chart.clientWidth;
          renderChart(current());
        }
      }).observe(chart);
    }
  }

  function init() {
    var hosts = document.querySelectorAll(".pc-battery-compare");
    if (!hosts.length || !SCRIPT) return;
    fetch(SCRIPT.getAttribute("data-battery-compare-data"))
      .then(function (response) {
        if (!response.ok) throw new Error("HTTP " + response.status);
        return response.json();
      })
      .then(function (data) {
        Array.prototype.forEach.call(hosts, function (host) { mount(host, data); });
      })
      .catch(function (error) {
        Array.prototype.forEach.call(hosts, function (host) { host.classList.add("pcbc-ready"); });
        if (window.console) console.warn("battery comparison could not load its data", error);
      });
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", init);
  } else {
    init();
  }
})();
